/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { camelCase } from 'lodash';

import { ActionButton, BrandColors, LinkButton, Tooltip } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { SectionCard } from '../SectionCard';
import { TaskError } from './TaskError';
import { DropdownButton } from 'blb-components/Button/DropdownButton';
import { VerticalSectionCard } from '../VerticalSectionCard';

const styles = StyleSheet.create({
  spacer: {
    ':not(:last-child)': {
      marginBottom: 16,
    },
  },
});

export interface TaskProps extends BlendBridge.API.BaseTask {
  isButtonLoading?: boolean;
  onClick: () => void;
  taskPreOpeningSubheader?: string;
  extraActions?: { label: string; onClick: () => void }[];
}

export const Task: React.FC<TaskProps> = ({
  displayType = 'card',
  taskHeader,
  isDisabled,
  disabledReason,
  onClick,
  error,
  buttonLabel = '',
  isButtonLoading,
  taskId,
  children,
  taskPreOpeningSubheader,
  extraActions,
  taskSubheader,
}) => {
  const id = taskId || camelCase(taskHeader);
  const sectionId = `it-task-${id}`;
  const buttonId = `${sectionId}-open`;

  const getButton = () => {
    if (extraActions && extraActions.length > 0) {
      return (
        <DropdownButton
          disabled={isDisabled}
          extraActions={extraActions}
          id={buttonId}
          isLoading={isButtonLoading}
          label={buttonLabel}
          onClick={onClick}
        />
      );
    } else {
      return (
        <ActionButton
          disabled={isDisabled}
          id={buttonId}
          isLoading={isButtonLoading}
          label={buttonLabel}
          onClick={onClick}
        />
      );
    }
  };
  return (
    <div className={css(styles.spacer)}>
      {error && <TaskError errorMessage={error} showDismiss={true} spacing="sm" />}
      {displayType === 'card' && (
        <SectionCard
          header={taskHeader}
          id={sectionId}
          sectionActions={<Tooltip text={disabledReason}>{getButton()}</Tooltip>}
          subtitle={taskPreOpeningSubheader}
        />
      )}
      {displayType === 'verticalCard' && (
        <VerticalSectionCard
          buttonLabel={buttonLabel}
          data-testid="vertical-section-card"
          description={taskSubheader}
          disabled={isDisabled}
          disabledReason={disabledReason}
          onButtonClick={onClick}
          title={taskHeader}
        />
      )}
      {displayType === 'link' && (
        <LinkButton color={BrandColors.SKY_TONE_45} icon="add" id={buttonId} label={taskHeader} onClick={onClick} />
      )}
      {children}
    </div>
  );
};
